import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { tools, getToolBySlug, Tool } from '../data/tools';

const ComparePage: React.FC = () => {
  const { slugA, slugB } = useParams<{ slugA: string; slugB: string }>();
  const navigate = useNavigate();

  const toolA = slugA && slugA !== '_' ? getToolBySlug(slugA) : undefined;
  const toolB = slugB && slugB !== '_' ? getToolBySlug(slugB) : undefined;

  const [pickerTarget, setPickerTarget] = useState<'A' | 'B' | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  const filteredPickerTools = tools.filter(t =>
    t.name.toLowerCase().includes(pickerSearch.toLowerCase()) &&
    t.slug !== (pickerTarget === 'A' ? slugB : slugA)
  );

  const handlePickTool = (slug: string) => {
    const effectiveA = slugA && slugA !== '_' ? slugA : '_';
    const effectiveB = slugB && slugB !== '_' ? slugB : '_';
    if (pickerTarget === 'A') {
      navigate(`/compare/${slug}/${effectiveB}`);
    } else {
      navigate(`/compare/${effectiveA}/${slug}`);
    }
    setPickerTarget(null);
    setPickerSearch('');
  };

  const renderPicker = (side: 'A' | 'B', tool: Tool | undefined) => (
    <div style={{ flex: 1 }}>
      {tool ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: 'var(--space-lg)', backgroundColor: 'var(--color-card)', borderRadius: 'var(--border-radius)', border: '2px solid var(--color-primary)' }}>
          <img
            src={`/logos/${tool.slug}.png`}
            alt={tool.name}
            style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '4px' }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{tool.name}</h3>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{tool.oneLiner}</p>
          </div>
          <button
            onClick={() => setPickerTarget(side)}
            style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius)', padding: 'var(--space-xs) var(--space-sm)', cursor: 'pointer', color: 'var(--color-text-muted)', fontFamily: 'var(--font-primary)', fontSize: '0.8rem' }}
          >
            Change
          </button>
        </div>
      ) : (
        <button
          onClick={() => setPickerTarget(side)}
          style={{
            width: '100%', padding: 'var(--space-xl)', backgroundColor: 'var(--color-card)',
            border: '2px dashed var(--color-border)', borderRadius: 'var(--border-radius)',
            cursor: 'pointer', color: 'var(--color-text-muted)', fontFamily: 'var(--font-primary)',
            fontSize: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-sm)'
          }}
        >
          <span style={{ fontSize: '2rem' }}>+</span>
          <span>Choose a tool</span>
        </button>
      )}
    </div>
  );

  const renderCell = (value: React.ReactNode, highlight?: boolean) => (
    <div style={{
      flex: 1,
      padding: 'var(--space-md)',
      backgroundColor: highlight ? 'rgba(227, 83, 54, 0.06)' : 'transparent',
      borderRadius: 'var(--border-radius)',
    }}>
      {value}
    </div>
  );

  const Row = ({ label, a, b, highlight }: { label: string; a: React.ReactNode; b: React.ReactNode; highlight?: boolean }) => (
    <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'stretch', borderBottom: '1px solid var(--color-border)', padding: 'var(--space-sm) 0' }}>
      <div style={{ width: '160px', flexShrink: 0, padding: 'var(--space-md)', color: 'var(--color-text-muted)', fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
        {label}
      </div>
      {renderCell(a, highlight)}
      <div style={{ width: '1px', backgroundColor: 'var(--color-border)', flexShrink: 0 }} />
      {renderCell(b, highlight)}
    </div>
  );

  const TagList = ({ items, color }: { items: string[]; color?: string }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
      {items.map((item, i) => (
        <span key={i} style={{
          fontSize: '0.78rem', padding: '2px 10px',
          backgroundColor: color || 'var(--color-secondary)',
          color: 'var(--color-bg)', borderRadius: '999px', fontWeight: '600'
        }}>{item}</span>
      ))}
    </div>
  );

  const BulletList = ({ items, icon, color }: { items: string[]; icon: string; color: string }) => (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)', fontSize: '0.9rem', color: 'var(--color-text-secondary)', alignItems: 'flex-start' }}>
          <span style={{ color, flexShrink: 0 }}>{icon}</span>
          {item}
        </li>
      ))}
    </ul>
  );

  const easeColor = (ease: string) => {
    if (ease === 'Beginner') return 'var(--color-success)';
    if (ease === 'Intermediate') return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  return (
    <div style={{ padding: '2rem 0 4rem' }}>
      <Helmet>
        {toolA && toolB ? (
          <>
            <title>{toolA.name} vs {toolB.name} (2026) — Side-by-Side Comparison | ChooseAI</title>
            <meta name="description" content={`Compare ${toolA.name} and ${toolB.name} side by side. Pricing, features, pros and cons, ease of use, and integrations — all in one place.`} />
            <meta property="og:title" content={`${toolA.name} vs ${toolB.name} (2026) | ChooseAI`} />
            <meta property="og:description" content={`Compare ${toolA.name} and ${toolB.name} side by side. Pricing, features, pros and cons, ease of use, and integrations — all in one place.`} />
            <meta property="og:url" content={`https://chooseai.co.uk/compare/${toolA.slug}/${toolB.slug}`} />
            <link rel="canonical" href={`https://chooseai.co.uk/compare/${toolA.slug}/${toolB.slug}`} />
          </>
        ) : (
          <>
            <title>Compare AI Tools — Side-by-Side | ChooseAI</title>
            <meta name="description" content="Compare any two AI tools side by side on ChooseAI. Pricing, features, pros and cons, ease of use, and integrations all in one place." />
            <link rel="canonical" href="https://chooseai.co.uk/compare" />
          </>
        )}
      </Helmet>
      <div className="container">
        {/* Back */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <Link to="/" style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>← Back to Home</Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: 'var(--space-sm)' }}>Compare AI Tools</h1>
          <p style={{ color: 'var(--color-text-muted)' }}>Select two tools to see a detailed side-by-side comparison</p>
        </div>

        {/* Tool Pickers */}
        <div style={{ display: 'flex', gap: 'var(--space-lg)', alignItems: 'stretch', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
          {renderPicker('A', toolA)}
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 var(--space-sm)', color: 'var(--color-text-muted)', fontWeight: '700', fontSize: '1.2rem', flexShrink: 0 }}>
            vs
          </div>
          {renderPicker('B', toolB)}
        </div>

        {/* Picker Modal */}
        {pickerTarget && (
          <div
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-lg)' }}
            onClick={() => { setPickerTarget(null); setPickerSearch(''); }}
          >
            <div
              style={{ backgroundColor: 'var(--color-bg)', borderRadius: 'var(--border-radius)', border: '1px solid var(--color-border)', padding: 'var(--space-xl)', width: '100%', maxWidth: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Choose Tool {pickerTarget}</h3>
                <button onClick={() => { setPickerTarget(null); setPickerSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)' }}>✕</button>
              </div>
              <input
                autoFocus
                type="text"
                placeholder="Search tools..."
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                style={{ padding: 'var(--space-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius)', backgroundColor: 'var(--color-card)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-primary)', fontSize: '1rem' }}
              />
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                {filteredPickerTools.map(tool => (
                  <button
                    key={tool.slug}
                    onClick={() => handlePickTool(tool.slug)}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', padding: 'var(--space-sm) var(--space-md)', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-primary)', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-primary)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                  >
                    <img src={`/logos/${tool.slug}.png`} alt={tool.name} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '3px', flexShrink: 0 }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--color-text-primary)', fontSize: '0.95rem' }}>{tool.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{tool.categories.slice(0, 2).join(' · ')}</div>
                    </div>
                  </button>
                ))}
                {filteredPickerTools.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 'var(--space-lg)' }}>No tools found</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Comparison Table */}
        {toolA && toolB && (
          <div className="card" style={{ padding: 'var(--space-xl)', marginBottom: 0 }}>
            {/* Sticky header */}
            <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)', paddingBottom: 'var(--space-lg)', borderBottom: '2px solid var(--color-primary)' }}>
              <div style={{ width: '160px', flexShrink: 0 }} />
              {[toolA, toolB].map(tool => (
                <div key={tool.slug} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <img src={`/logos/${tool.slug}.png`} alt={tool.name} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'contain', backgroundColor: '#FDF8F0', padding: '4px', border: '1px solid var(--color-border)' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{tool.name}</h3>
                    <Link to={`/tools/${tool.slug}`} style={{ fontSize: '0.8rem', color: 'var(--color-primary)' }}>View full profile →</Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Overview */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)' }}>Overview</h4>

            <Row label="One-liner"
              a={<p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>{toolA.oneLiner}</p>}
              b={<p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>{toolB.oneLiner}</p>}
            />
            <Row label="Categories"
              a={<TagList items={toolA.categories} />}
              b={<TagList items={toolB.categories} />}
            />
            <Row label="Best For"
              a={<TagList items={toolA.bestFor} color="#F7B899" />}
              b={<TagList items={toolB.bestFor} color="#F7B899" />}
            />

            {/* Ease & Access */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Ease of Use</h4>

            <Row label="Skill Level"
              a={<span style={{ fontWeight: '700', color: easeColor(toolA.easeOfUse) }}>{toolA.easeOfUse}</span>}
              b={<span style={{ fontWeight: '700', color: easeColor(toolB.easeOfUse) }}>{toolB.easeOfUse}</span>}
              highlight={toolA.easeOfUse !== toolB.easeOfUse}
            />
            <Row label="Getting Started"
              a={<span style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>{toolA.gettingStartedTime}</span>}
              b={<span style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>{toolB.gettingStartedTime}</span>}
              highlight={toolA.gettingStartedTime !== toolB.gettingStartedTime}
            />

            {/* Pricing */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Pricing</h4>

            <Row label="Summary"
              a={<p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>{toolA.pricing.summary}</p>}
              b={<p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>{toolB.pricing.summary}</p>}
            />
            <Row label="Tiers"
              a={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                  {toolA.pricing.tiers.map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--color-text-primary)', fontWeight: '600' }}>{t.name}</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: '700' }}>{t.price}</span>
                    </div>
                  ))}
                </div>
              }
              b={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                  {toolB.pricing.tiers.map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--color-text-primary)', fontWeight: '600' }}>{t.name}</span>
                      <span style={{ color: 'var(--color-primary)', fontWeight: '700' }}>{t.price}</span>
                    </div>
                  ))}
                </div>
              }
            />

            {/* Pros & Cons */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Pros & Cons</h4>

            <Row label="Pros"
              a={<BulletList items={toolA.pros} icon="✓" color="var(--color-success)" />}
              b={<BulletList items={toolB.pros} icon="✓" color="var(--color-success)" />}
            />
            <Row label="Cons"
              a={<BulletList items={toolA.cons} icon="✗" color="var(--color-error)" />}
              b={<BulletList items={toolB.cons} icon="✗" color="var(--color-error)" />}
            />

            {/* Fit */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Is It a Good Fit?</h4>

            <Row label="Good Fit If"
              a={<BulletList items={toolA.isThisForYou.goodFit} icon="✓" color="var(--color-success)" />}
              b={<BulletList items={toolB.isThisForYou.goodFit} icon="✓" color="var(--color-success)" />}
            />
            <Row label="Not Good Fit If"
              a={<BulletList items={toolA.isThisForYou.notGoodFit} icon="✗" color="var(--color-error)" />}
              b={<BulletList items={toolB.isThisForYou.notGoodFit} icon="✗" color="var(--color-error)" />}
            />

            {/* Integrations */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Integrations</h4>

            <Row label="Works With"
              a={<TagList items={toolA.integrations} color="#F7B899" />}
              b={<TagList items={toolB.integrations} color="#F7B899" />}
            />

            {/* Privacy */}
            <h4 style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', marginBottom: 'var(--space-sm)', marginTop: 'var(--space-xl)' }}>Data & Privacy</h4>

            <Row label="Data Handling"
              a={<p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{toolA.dataPrivacyAndSecurity.dataHandling}</p>}
              b={<p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{toolB.dataPrivacyAndSecurity.dataHandling}</p>}
            />
            <Row label="Privacy"
              a={<p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{toolA.dataPrivacyAndSecurity.privacy}</p>}
              b={<p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{toolB.dataPrivacyAndSecurity.privacy}</p>}
            />
            <Row label="Security"
              a={<BulletList items={toolA.dataPrivacyAndSecurity.security} icon="•" color="var(--color-primary)" />}
              b={<BulletList items={toolB.dataPrivacyAndSecurity.security} icon="•" color="var(--color-primary)" />}
            />

            {/* CTA row */}
            <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-xl)', paddingTop: 'var(--space-lg)', borderTop: '2px solid var(--color-border)' }}>
              <div style={{ width: '160px', flexShrink: 0 }} />
              {[toolA, toolB].map(tool => (
                <div key={tool.slug} style={{ flex: 1, display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <a href={tool.website} target="_blank" rel="noopener noreferrer" className="button button-primary" style={{ fontSize: '0.9rem' }}>
                    Visit {tool.name}
                  </a>
                  <Link to={`/tools/${tool.slug}`} className="button button-secondary" style={{ fontSize: '0.9rem' }}>
                    Full Profile
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {(!toolA || !toolB) && (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--color-text-muted)' }}>
            <p style={{ fontSize: '1.1rem' }}>Select two tools above to start comparing</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
